import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import AdminPageWrapper from './components/AdminPageWrapper';
import ProfessionalTable from '../components/ProfessionalTable';
import api from '../utils/api';
import { API } from '../config/apiConfig';

export default function AdminChangeParent() {
  const [lookup, setLookup] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [matches, setMatches] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [user, setUser] = useState(null);
  const [referredByCode, setReferredByCode] = useState('');
  const [originalReferredByCode, setOriginalReferredByCode] = useState('');
  const [parentSearch, setParentSearch] = useState('');
  const [parentMatches, setParentMatches] = useState([]);
  const [parentSearching, setParentSearching] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLines, setConfirmLines] = useState([]);
  const [pendingBody, setPendingBody] = useState(null);

  const applyParentUser = (u) => {
    setUser(u);
    setReferredByCode(u.referredByCode || '');
    setOriginalReferredByCode(u.originalReferredByCode || '');
  };

  const loadParentsById = async (userId) => {
    const res = await api.get(API.adminUserParents(String(userId)));
    if (!res.data?.success || !res.data.user) {
      throw new Error(res.data?.error || 'User not found');
    }
    applyParentUser(res.data.user);
  };

  const searchUsers = async () => {
    const key = lookup.trim();
    if (!key) {
      setError('Enter username, name, email, UserID, or referral code.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    setUser(null);
    setMatches([]);
    setHasSearched(true);
    try {
      const res = await api.get(API.adminUsers, {
        params: { searchTerm: key },
      });
      const found = res.data?.users || [];
      setMatches(found);
      if (found.length === 1) {
        await loadParentsById(found[0].UserID);
      } else if (found.length === 0) {
        try {
          const parentRes = await api.get(API.adminUserParents(key));
          if (parentRes.data?.success && parentRes.data.user) {
            applyParentUser(parentRes.data.user);
          } else {
            setError('No users found');
          }
        } catch {
          setError('No users found');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!user) return;
    const place = referredByCode.trim();
    const orig = originalReferredByCode.trim();
    const body = { userId: user.userId };
    const placeChanged = place !== String(user.referredByCode || '').trim();
    const origChanged = orig !== String(user.originalReferredByCode || '').trim();
    if (!placeChanged && !origChanged) {
      setError('Change at least one parent code.');
      return;
    }
    if (placeChanged) body.referredByCode = place;
    if (origChanged) body.originalReferredByCode = orig;
    const bits = [];
    if (placeChanged) bits.push(`ReferredByCode → ${place}`);
    if (origChanged) bits.push(`OriginalReferredByCode → ${orig}`);
    setPendingBody(body);
    setConfirmLines(bits);
    setConfirmOpen(true);
  };

  const confirmApply = async () => {
    if (!user || !pendingBody) return;
    setConfirmOpen(false);
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post(API.adminSetUserParents, pendingBody);
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Update failed');
      }
      setSuccess(res.data.message || 'Updated');
      const again = await api.get(API.adminUserParents(String(user.userId)));
      if (again.data?.user) {
        const u = again.data.user;
        setUser(u);
        setReferredByCode(u.referredByCode || '');
        setOriginalReferredByCode(u.originalReferredByCode || '');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setSaving(false);
      setPendingBody(null);
    }
  };

  const searchParents = async () => {
    const key = parentSearch.trim();
    if (!key) {
      setError('Enter username, name, or email to find a parent.');
      return;
    }
    setParentSearching(true);
    setError('');
    try {
      const res = await api.get(API.adminUsers, { params: { searchTerm: key } });
      setParentMatches(res.data?.users || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Parent search failed');
      setParentMatches([]);
    } finally {
      setParentSearching(false);
    }
  };

  const useMatchAsParent = async (row, which) => {
    try {
      const res = await api.get(API.adminUserParents(String(row.UserID)));
      const code = res.data?.user?.referralCode;
      if (!code) {
        setError('That user has no referral code.');
        return;
      }
      if (which === 'place') setReferredByCode(code);
      else setOriginalReferredByCode(code);
      setSuccess(
        which === 'place'
          ? `ReferredByCode set to ${code}`
          : `OriginalReferredByCode set to ${code}`
      );
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not load referral code');
    }
  };

  return (
    <AdminPageWrapper
      title="Change parent"
      subtitle="Set placement seat (ReferredByCode) and/or signup parent (OriginalReferredByCode), then refresh the tree cache."
    >
      <Stack spacing={2}>
        {error ? (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        ) : null}

        <Paper sx={{ p: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              label="Username, name, email, UserID, or referral code"
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void searchUsers();
              }}
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={() => void searchUsers()}
              disabled={loading || !lookup.trim()}
              sx={{ flexShrink: 0 }}
            >
              Search
            </Button>
          </Stack>
        </Paper>

        {!user && hasSearched ? (
          <ProfessionalTable
            columns={[
              {
                header: 'Username',
                accessor: 'UserName',
                render: (value) => <Typography fontWeight={500}>{value}</Typography>,
              },
              {
                header: 'Name',
                accessor: 'FirstName',
                render: (value, row) => (
                  <Box>
                    <Typography>{value || '—'}</Typography>
                    <Typography variant="body2" color="text.secondary">{row.LastName || '—'}</Typography>
                  </Box>
                ),
              },
              {
                header: 'Email',
                accessor: 'Email',
                render: (email) => <Typography variant="body2">{email}</Typography>,
              },
            ]}
            data={matches.map((u) => ({ ...u, id: u.UserID }))}
            onRowClick={async (row) => {
              setLoading(true);
              setError('');
              try {
                await loadParentsById(row.UserID);
              } catch (err) {
                setError(err.response?.data?.error || err.message || 'Lookup failed');
              } finally {
                setLoading(false);
              }
            }}
            loading={loading}
            emptyMessage="No users found"
          />
        ) : null}

        {user ? (
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {user.name || user.userName} · ID {user.userId}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Their referral code: {user.referralCode || '—'}
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="ReferredByCode (placement seat)"
                value={referredByCode}
                onChange={(e) => setReferredByCode(e.target.value)}
                helperText={
                  user.placementParentName
                    ? `Current placement parent: ${user.placementParentName}. Change this, OriginalReferredByCode, or both.`
                    : 'Who they sit under in the 2-wide tree. Change this, OriginalReferredByCode, or both.'
                }
                fullWidth
              />
              <TextField
                label="OriginalReferredByCode (who signed them up)"
                value={originalReferredByCode}
                onChange={(e) => setOriginalReferredByCode(e.target.value)}
                helperText={
                  user.signupParentName
                    ? `Current signup parent: ${user.signupParentName}. Independent of placement.`
                    : 'Community / calculator / My Referrals. Independent of placement.'
                }
                fullWidth
              />

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Find a parent by username, name, or email
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Parent username, name, or email"
                    value={parentSearch}
                    onChange={(e) => setParentSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void searchParents();
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={parentSearching ? <CircularProgress size={16} /> : <SearchIcon />}
                    onClick={() => void searchParents()}
                    disabled={parentSearching || !parentSearch.trim()}
                    sx={{ flexShrink: 0 }}
                  >
                    Find
                  </Button>
                </Stack>
                {parentMatches.length > 0 ? (
                  <ProfessionalTable
                    columns={[
                      {
                        header: 'Username',
                        accessor: 'UserName',
                        render: (value) => <Typography fontWeight={500}>{value}</Typography>,
                      },
                      {
                        header: 'Name',
                        accessor: 'FirstName',
                        render: (value, row) => (
                          <Typography>{[value, row.LastName].filter(Boolean).join(' ') || '—'}</Typography>
                        ),
                      },
                      {
                        header: 'Set as',
                        accessor: 'UserID',
                        render: (_id, row) => (
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="contained" onClick={() => void useMatchAsParent(row, 'place')}>
                              ReferredByCode
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => void useMatchAsParent(row, 'orig')}>
                              OriginalReferredByCode
                            </Button>
                          </Stack>
                        ),
                      },
                    ]}
                    data={parentMatches.map((u) => ({ ...u, id: u.UserID }))}
                    loading={parentSearching}
                    emptyMessage="No parent users found"
                  />
                ) : null}
              </Paper>

              <Box>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={() => void apply()}
                  disabled={saving}
                >
                  Apply and refresh cache
                </Button>
              </Box>
            </Stack>
          </Paper>
        ) : null}
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm parent change</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            Update {user?.name || user?.userName} (ID {user?.userId})?
          </Typography>
          {confirmLines.map((line) => (
            <Typography key={line} variant="body2">
              {line}
            </Typography>
          ))}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Downline cache will be rebuilt for this branch.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void confirmApply()} disabled={saving}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPageWrapper>
  );
}
